import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { fetchCommercialPdf, fetchQuote, submitCommercial } from '../Api/QuoteApi';
import { SERVICE_AGREEMENT, buildCommercialConsentText } from '../_lib/agreement-clauses';
import { commercialProposalContent } from '../_lib/commercial-prepared';
import CommercialProposalSections, { CommercialCover, CommercialFooter, SectionHeading } from './CommercialProposalSections';
import '../styles/commercial-agreement.css';

const EMPTY_SETUP = {
  propertyName: '',
  billingContactName: '',
  billingContactEmail: '',
  billingContactPhone: '',
  onsiteContactName: '',
  onsiteContactEmail: '',
  onsiteContactPhone: '',
  startDate: '',
  propertyNotes: '',
  howHeard: '',
};

const CommercialAgreement = ({ config }) => {
  const { dealId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [printFallback, setPrintFallback] = useState(false);
  const [pdfPreparing, setPdfPreparing] = useState(false);
  const [setup, setSetup] = useState(EMPTY_SETUP);
  const requestGeneration = useRef(0);
  const submissionInFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    requestGeneration.current += 1;
    submissionInFlight.current = false;
    setLoading(true);
    setQuote(null);
    setError(null);
    setSetup(EMPTY_SETUP);
    setSignerName('');
    setSignerTitle('');
    setAgreed(false);
    setSubmitting(false);
    setSubmitError(null);
    fetchQuote(dealId, { context: 'commercial' })
      .then((data) => {
        if (cancelled) return;
        if (data?.isCommercial !== true) {
          setError('Agreement not found.');
          return;
        }
        setQuote(data);
        setSetup(Object.fromEntries(Object.keys(EMPTY_SETUP).map((key) => [
          key, typeof data[key] === 'string' ? data[key] : '',
        ])));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status === 404 ? 'Agreement not found.' : 'Unable to load agreement.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      requestGeneration.current += 1;
    };
  }, [dealId]);

  const consentText = buildCommercialConsentText({ propertyName: setup.propertyName, terms: quote?.terms, prepared: quote?.prepared });
  const esignDisclosure = SERVICE_AGREEMENT.commercialEsignDisclosure;
  const regional = quote?.regional || null;
  const phone = regional?.phone || config.phone;
  const tel = regional?.tel || `tel:${String(config.phone || '').replace(/[^0-9+]/g, '')}`;
  const telHref = tel.startsWith('tel:') ? tel : `tel:${tel}`;

  const canSign = useMemo(
    () => setup.propertyName.trim() && setup.billingContactName.trim() && setup.billingContactEmail.trim()
      && signerName.trim() && signerTitle.trim() && agreed && !submitting,
    [setup, signerName, signerTitle, agreed, submitting],
  );

  const updateSetup = (event) => {
    const { name, value } = event.target;
    setSetup((current) => ({ ...current, [name]: value }));
  };

  const handleSign = async (event) => {
    event.preventDefault();
    if (!canSign || submissionInFlight.current || !event.currentTarget.reportValidity()) return;
    const generation = requestGeneration.current;
    submissionInFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    let signatureAccepted = false;
    try {
      await submitCommercial(dealId, {
        signerName: signerName.trim(),
        signerTitle: signerTitle.trim(),
        setup: Object.fromEntries(Object.entries(setup).map(([key, value]) => [key, value.trim()])),
        consentAccepted: true,
        agreementRevision: quote.agreementRevision,
        templateVersion: quote.templateVersion,
      });
      signatureAccepted = true;
      if (generation !== requestGeneration.current) return;
      const savedQuote = await fetchQuote(dealId, { context: 'commercial' });
      if (generation !== requestGeneration.current) return;
      if (savedQuote?.isCommercial !== true || !savedQuote.signedAt || !savedQuote.signedAgreementSnapshot) {
        throw new Error('Signed agreement could not be loaded');
      }
      setQuote(savedQuote);
    } catch (err) {
      if (generation !== requestGeneration.current) return;
      Sentry.captureException(err, { tags: { stage: 'commercial_sign_submit' } });
      setSubmitError(signatureAccepted
        ? {
            message: 'Your signing request was accepted, but we could not load the saved agreement. Reload to view the recorded details.',
            reload: true,
          }
        : err?.response?.status === 409
        ? {
            message: 'This proposal may have changed, or another signing attempt is still in progress. Reload the proposal and review the latest details before signing.',
            reload: true,
          }
        : {
            message: 'Something went wrong signing the agreement. Please try again, or reply to your quote email and we will help.',
            reload: false,
          });
    } finally {
      if (generation === requestGeneration.current) {
        submissionInFlight.current = false;
        setSubmitting(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="cp-loading">
        <img src={config.logo} alt={config.name} />
        <div>Loading your proposal...</div>
      </div>
    );
  }
  if (error || !quote || quote.isCommercial !== true) {
    return (
      <div className="cp-error-page">
        <img src={config.logo} alt={config.name} />
        <h2>{error || 'Agreement not found.'}</h2>
      </div>
    );
  }

  const validUntilMs = Date.parse(quote.validUntil || '');
  const invalidExpiry = quote.status === 'pending' && !Number.isFinite(validUntilMs);
  const expiredByTime = quote.status === 'pending' && Number.isFinite(validUntilMs) && validUntilMs <= Date.now();
  const knownStatus = ['pending', 'signing', 'signed', 'expired', 'revoked', 'declined', 'cancelled'].includes(quote.status);
  const terminalStatus = invalidExpiry || !knownStatus
    ? 'unavailable'
    : expiredByTime
      ? 'expired'
      : ['expired', 'revoked', 'declined', 'cancelled'].includes(quote.status) ? quote.status : null;
  if (terminalStatus) {
    const labels = {
      expired: ['Proposal expired', 'This proposal is past its signing date.'],
      revoked: ['Proposal withdrawn', 'This version was withdrawn by Cabana.'],
      declined: ['Proposal declined', 'This proposal has been recorded as declined.'],
      cancelled: ['Proposal cancelled', 'This proposal is no longer available to sign.'],
      unavailable: ['Proposal unavailable', 'This proposal does not have a valid signing window.'],
    };
    return (
      <main className="cp-root">
        <div className="cp-state-page" role="status">
          <img src={config.logo} alt={config.name} />
          <div className="cp-state-label">Commercial service proposal</div>
          <h1>{labels[terminalStatus][0]}</h1>
          <p>{labels[terminalStatus][1]} No signature or payment has been taken.</p>
          <p>For a current version, reply to your quote email or call <a href={telHref}>{phone}</a>.</p>
        </div>
      </main>
    );
  }

  if (['signing', 'signed'].includes(quote.status) && !(quote.signedAt && quote.signedAgreementSnapshot)) {
    return (
      <main className="cp-root">
        <div className="cp-state-page" role="status" aria-live="polite">
          <img src={config.logo} alt={config.name} />
          <div className="cp-state-label">Commercial service proposal</div>
          <h1>Signing is being recorded</h1>
          <p>Please wait a moment, then reload this page to open the saved agreement. Do not sign again.</p>
        </div>
      </main>
    );
  }

  if (quote.signedAt && quote.signedAgreementSnapshot) {
    const signedSetup = quote.signedAgreementSnapshot;
    const contacts = signedSetup.contacts || {};
    const recordedName = (value) => (typeof value === 'string' ? value.trim() : '');
    const schedulingContact = recordedName(contacts.onsiteContactName) || recordedName(contacts.billingContactName);
    const propertyName = signedSetup.propertyName;
    return (
      <div className="cp-root">
        <div className="cp-signed">
          <img src={config.logo} alt={config.name} style={{ height: 40 }} />
          <div className="cp-signed-check" aria-hidden="true">&#10003;</div>
          <h1 className="cp-signed-title">Agreement signed</h1>
          <p className="cp-signed-sub">
            Thank you. Your agreement is signed and saved. Our team will reach out
            to {schedulingContact || 'your on-site contact'} to schedule the
            first visit for {propertyName || 'your property'}.
          </p>
          {/* dealId (route param) already carries "<id>-<token>"; do not append the token again. */}
          <p className="cp-signed-sub">
            <a href={`/commercial-agreement/${dealId}`}>View or print your signed copy</a>
          </p>
          <div className="cp-footer cp-footer-plain">{config.name} · {phone}</div>
        </div>
      </div>
    );
  }

  const draftQuote = { ...quote, ...setup };

  // The browser's save-as-PDF names the file from document.title, so title it
  // after the proposal for the duration of the print dialog.
  const pdfTitle = `${config.name} proposal - ${draftQuote.propertyName || draftQuote.billingContactName || dealId.split('-')[0]}`;

  const printPdf = () => {
    const previousTitle = document.title;
    document.title = pdfTitle;
    window.addEventListener('afterprint', () => { document.title = previousTitle; }, { once: true });
    try { window.print(); setPrintFallback(false); }
    catch (err) {
      document.title = previousTitle;
      setPrintFallback(true);
      Sentry.captureMessage('commercial_proposal_print_fallback', { level: 'warning', extra: { error: String(err) } });
    }
  };

  // The server renders the same file for every device; browser print is
  // only the fallback when that render fails.
  const handleDownloadPdf = async () => {
    if (pdfPreparing) return;
    setPdfPreparing(true);
    setPrintFallback(false);
    try {
      const blob = await fetchCommercialPdf(dealId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${pdfTitle.replace(/[\\/:*?"<>|]+/g, ' ')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      Sentry.captureMessage('commercial_proposal_pdf_render_failed', { level: 'warning', extra: { status: err?.response?.status, error: String(err) } });
      printPdf();
    } finally {
      setPdfPreparing(false);
    }
  };

  return (
    <div className="cp-root">
      <CommercialCover config={config} q={draftQuote} />
      <div className="cp-body">
        <div className="cp-document-layout">
          <aside className="cp-summary-rail" aria-label="Proposal summary">
            <div className="cp-summary-property">{draftQuote.propertyName || draftQuote.billingContactName || 'Your property'}</div>
            <div className="cp-summary-term">Prepared for {draftQuote.billingContactName || 'the property team'}</div>
            <nav aria-label="Proposal contents">
              <a href="#cp-about-section">About Cabana</a>
              <a href="#cp-included-section">What is included</a>
              <a href="#cp-proposal-section">Pricing</a>
              <a href="#cp-terms-section">Terms</a>
              <a href="#cp-setup-section">Setup and signature</a>
            </nav>
            <a className="cp-summary-action" href="#cp-setup-section">Review and sign</a>
          </aside>

          <main className="cp-document">
            <div className="cp-proposal-tools no-print">
              <button type="button" className="cp-print-btn" onClick={handleDownloadPdf} disabled={pdfPreparing} aria-busy={pdfPreparing}>{pdfPreparing ? 'Preparing your PDF...' : 'Download PDF'}</button>
            </div>
            {printFallback && (
              <div className="cp-error no-print" role="alert">
                Saving a PDF isn't supported in this in-app browser. Open this page in Safari or Chrome and download it from there.
              </div>
            )}
            <CommercialProposalSections config={config} q={draftQuote} content={commercialProposalContent(SERVICE_AGREEMENT.commercial, draftQuote.prepared)} />

            <p className="cp-print-only cp-print-cta"><strong>Ready to get started?</strong> Review and sign online using the link in your proposal email, or call us at {phone}.</p>
            <form className="no-print" onSubmit={handleSign} aria-label="Commercial service agreement" aria-busy={submitting}>
          <fieldset className="cp-form-fields" disabled={submitting} aria-label="Service setup and signature">
            <section className="cp-section" id="cp-setup-section">
              <SectionHeading eyebrow="Before we start">Service setup</SectionHeading>
              {quote.prepared && <p className="cp-p">These contacts are part of your prepared proposal. Contact Cabana for a revision if they need to change.</p>}
              <div className="cp-setup">
                <div className="cp-setup-card">
                  <div className="cp-setup-head">Property and billing</div>
                  <label className="cp-field cp-setup-field">
                    <span>Property name *</span>
                    <input readOnly={!!quote.prepared} name="propertyName" value={setup.propertyName} onChange={updateSetup} required maxLength={200} autoComplete="organization" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>Billing contact name *</span>
                    <input readOnly={!!quote.prepared} name="billingContactName" value={setup.billingContactName} onChange={updateSetup} required maxLength={200} autoComplete="section-billing name" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>Billing email *</span>
                    <input readOnly={!!quote.prepared} name="billingContactEmail" type="email" value={setup.billingContactEmail} onChange={updateSetup} required maxLength={254} autoComplete="section-billing email" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>Billing phone</span>
                    <input readOnly={!!quote.prepared} name="billingContactPhone" type="tel" value={setup.billingContactPhone} onChange={updateSetup} maxLength={80} autoComplete="section-billing tel" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>How did you hear about us?</span>
                    <textarea name="howHeard" value={setup.howHeard} onChange={updateSetup} maxLength={500} rows={3} />
                  </label>
                </div>
                <div className="cp-setup-card">
                  <div className="cp-setup-head">On-site and service details</div>
                  <label className="cp-field cp-setup-field">
                    <span>On-site contact name</span>
                    <input readOnly={!!quote.prepared} name="onsiteContactName" value={setup.onsiteContactName} onChange={updateSetup} maxLength={200} autoComplete="section-onsite name" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>On-site email</span>
                    <input readOnly={!!quote.prepared} name="onsiteContactEmail" type="email" value={setup.onsiteContactEmail} onChange={updateSetup} maxLength={254} autoComplete="section-onsite email" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>On-site phone</span>
                    <input readOnly={!!quote.prepared} name="onsiteContactPhone" type="tel" value={setup.onsiteContactPhone} onChange={updateSetup} maxLength={80} autoComplete="section-onsite tel" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>Requested start date</span>
                    <input name="startDate" type="date" value={setup.startDate} onChange={updateSetup} max="9999-12-31" />
                  </label>
                  <label className="cp-field cp-setup-field">
                    <span>Property notes</span>
                    <textarea name="propertyNotes" value={setup.propertyNotes} onChange={updateSetup} maxLength={2000} rows={3} placeholder="Gate codes, lock box codes, parking instructions, key access, etc." />
                  </label>
                </div>
              </div>
            </section>

            {/* Service agreement + sign */}
            <section className="cp-section" id="cp-sign-section">
              <SectionHeading eyebrow="Sign">Service agreement</SectionHeading>
              <div className="cp-agree-box">
                By signing below, {setup.propertyName || 'the property named above'} authorizes {config.name} to
                provide the services and payment terms outlined in this proposal. This is a month-to-month
                agreement you can cancel anytime with 30 days written notice.
              </div>
              <div className="cp-sign">
                <div className="cp-sign-fields">
                  <label className="cp-field">
                    <span>Your full name *</span>
                    <input name="signerName" type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)} required maxLength={200} placeholder="First and last name" autoComplete="section-signer name" />
                  </label>
                  <label className="cp-field">
                    <span>Your title *</span>
                    <input name="signerTitle" type="text" value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} required maxLength={200} placeholder="e.g. Community Manager, Owner" autoComplete="organization-title" />
                  </label>
                </div>
                <label className="cp-consent">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />
                  <span>{consentText}</span>
                </label>
                <p className="cp-esign">{esignDisclosure}</p>
                <p className="cp-correction">Need a quote correction? Do not sign this version. Reply to your quote email or call <a href={telHref}>{phone}</a> and we will send an updated proposal.</p>
                {submitError && (
                  <div className="cp-error" role="alert">
                    <p className="cp-p">{submitError.message}</p>
                    {submitError.reload && (
                      <button type="button" className="cp-print-btn" onClick={() => window.location.reload()}>
                        Reload proposal
                      </button>
                    )}
                  </div>
                )}
                <button type="submit" className="cp-sign-btn" disabled={!canSign}>
                  {submitting ? 'Signing...' : 'Sign agreement'}
                </button>
                {signerName && <div className="cp-sig-preview" aria-hidden="true">{signerName}</div>}
              </div>
            </section>
          </fieldset>
            </form>
            <CommercialFooter config={config} regional={regional} />
          </main>
        </div>
      </div>

      {/* Sticky primary action: keeps signing reachable through the long
          proposal without competing with any other action. */}
      <div className="cp-review-bar">
        <div className="cp-review-summary">
          <strong>{draftQuote.propertyName || 'This property'}</strong>
        </div>
        <button
          type="button"
          className="cp-review-cta"
          onClick={() => {
            const el = document.getElementById('cp-setup-section');
            if (el) {
              el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
              el.querySelector('input')?.focus({ preventScroll: true });
            }
          }}
        >
          Review and sign
        </button>
      </div>
    </div>
  );
};

export default CommercialAgreement;
